import { useEffect, useRef, useState } from 'react'
import classnames from 'classnames/bind'
import { useCustomEventListener } from 'react-custom-events'
import { Wrapper, Status } from '@googlemaps/react-wrapper'
import { Spinner } from '~/components'
import { qsa } from '~/utils'
// import { default as mapStyles } from './MapStyles'
import * as styles from './index.module.css'

const cx = classnames.bind(styles)

const settings = {
  // mapId: 'fcd856d6f8054356',
  mapId: process.env.GOOGLE_MAP_STYLE_ID || '5027ffb08a7a5883f8d89c81',
  center: { lat: 59.323569, lng: 18.06683 },
  zoom: 14,
  // bounds: null,
  // locations: {},
  // mapLoaded: false,
  // mapStarted: false
}

if (window.EMBED_CONFIG?.providerLocation) {
  settings.center = {
    lat: window.EMBED_CONFIG.providerLocation.latitude,
    lng: window.EMBED_CONFIG.providerLocation.longitude
  }
}

console.log(settings)
const formatPos = pos => {
  return { lat: pos.latitude, lng: pos.longitude }
}

// NOTE: Only for debug
const formatAddress = address => {
  return address.streetNumber 
    ? address.streetName + ' ' + address.streetNumber
    : address.streetName
}

const isMobile = () => window.innerWidth < 970

// On mobile the form overlays the bottom of the map. After panTo the marker
// sits at the map's geometric centre, which is behind the form. Pan up by
// half the form height so the marker ends up in the visible centre above it.
// In embed mode the form is stacked below the map (not overlaying), so no offset.
const getMobileBottomOffset = () => {
  if (window.EMBED_CONFIG?.isEmbed) return 0
  const form = document.querySelector('.booking-module-modern-form')
  return form ? form.offsetHeight / 2 : 150
}

// On desktop the form panel is right-aligned over the map, so the only visible
// map is the strip to its left. A marker panned to the geometric centre lands
// behind the panel — pan by half the inset to bring it into that strip.
// The value comes from --map-right-inset on .booking-module-modern so panel widths
// stay defined in one place: 724px normally, 942px fullscreen — which is what the
// embed uses, and why the old hardcoded 724/362 fell short there.
const DEFAULT_RIGHT_INSET = 724

const getDesktopRightOffset = () => {
  const bm = document.querySelector('.booking-module-modern')
  if (!bm) return DEFAULT_RIGHT_INSET
  const inset = parseInt(getComputedStyle(bm).getPropertyValue('--map-right-inset'), 10)
  return Number.isFinite(inset) && inset > 0 ? inset : DEFAULT_RIGHT_INSET
}

const MapComponent = () => {
  const elRef = useRef()
  const mapRef = useRef()
  const markersRef = useRef()
  const exceptionsRef = useRef()
  const listenersRef = useRef()
  const timeoutRef = useRef()
  const [isLoaded, setIsLoaded] = useState(false)


  const initMap = () => {
    console.log('Init map')

    mapRef.current = new window.google.maps.Map(elRef.current, {
      mapId: settings.mapId,
      gestureHandling: 'none',
      scrollwheel: false,
      disableDefaultUI: true,
      disableDoubleClickZoom: true,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      ...settings
      // styles: mapStyles,
    })

    createMarkers()

    window.google.maps.event.addListenerOnce(mapRef.current, 'tilesloaded', () => {
      console.log('Map loaded')
      setIsLoaded(true)
      // NOTE: set tabindex to -1 for all elements in the map to avoid scrolling bug
      setTimeout(() => {
        qsa('#map-modern button, #map-modern *[tabindex="0"]').forEach(el => {
          el.tabIndex = -1
        })
      }, 100)
      // window.google.maps.event.addListener(mapRef.current, 'idle', () => {
      //   console.log('Map is idle')
      // })
    })
  }

  const createMarkers = async () => {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker')
    markersRef.current = []
    listenersRef.current = []

    for (let i = 0; i < 10; i++) {
      const pin = new PinElement({
        background: '#000000',
        borderColor: '#000000',
        glyphColor: '#FFFFFF',
        // glyph: character,
        scale: 1.25,
      })

      const marker = new AdvancedMarkerElement({
        map: mapRef.current,
        // position: formatPos(location.address.position),
        content: pin.element,
      })

      markersRef.current.push({ marker, pin })      
      listenersRef.current.push(null)
    }
  }

  const drawExeptionsZone = async ({ paths, locations }) => {
    if (mapRef.current) {
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker')
      resetExceptionsZone();
      clearTimeout(timeoutRef.current);
      if(exceptionsRef.current) exceptionsRef.current.zone = new google.maps.Polygon({
        paths: paths,
        strokeColor: "#0074ce",
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: "#0074ce",
        fillOpacity: 0.15,
      })
      exceptionsRef.current?.zone?.setMap(mapRef.current)
      // mapRef.current.setZoom(14)

      locations?.forEach(location => {
        const pin = new PinElement({
          background: '#000000',
          borderColor: '#FFFFFF',
          glyphColor: '#FFFFFF',
          // glyph: '1',
          scale: 0.75,
        })

        const marker = new AdvancedMarkerElement({
          map: mapRef.current,
          position: location,
          content: pin.element,
        })

        exceptionsRef.current.locations.push({ marker, pin, locationId: location.locationId });
      });
      

      const bounds = new google.maps.LatLngBounds();
      paths.forEach(path => {
        bounds.extend(path);
      });
      mapRef.current.fitBounds(bounds);
      if (!isMobile()) {
        mapRef.current.panBy(getDesktopRightOffset() / 2, 0); // desktop: adjust for right-side form panel
      }
      // mapRef.current.panTo(new google.maps.LatLng(paths[0]));

    }
  }

  const resetExceptionsZone = () => {
    exceptionsRef?.current?.locations?.forEach(location => {
      location.marker.setMap(null);
    })
    if(exceptionsRef.current) exceptionsRef.current.locations = [];
    exceptionsRef?.current?.zone?.setMap(null);
    exceptionsRef.current = { locations: [] };
  }

  const hoverListItem = ({ item }) => {
    exceptionsRef.current?.locations?.forEach(location => {
      if (location?.locationId === item.id) {
        location.pin.background = '#febf00';
        location.pin.scale = 1;
      } else {
        location.pin.background = '#000000';
        location.pin.scale = 0.75;
      }
    })
    //exceptionsRef.current.locations[item.locationId].marker.content = exceptionsRef.current.locations[item.locationId].pin.element;
    
  }

  const clearHoverListItem = ({ item }) => {
    exceptionsRef?.current?.locations?.forEach(location => {
      location.pin.background = '#000000';
      location.pin.scale = 0.75;
    })
    //exceptionsRef.current.locations[item.locationId].marker.content = exceptionsRef.current.locations[item.locationId].pin.element;    
  }


  const handleLocationChange = ({ locations, active }) => {
    console.log("LOCATION CHANGE");
    resetExceptionsZone();
    if (mapRef.current) {
      clearTimeout(timeoutRef.current)
      locations = locations.slice(-10)

      // NOTE: only for debug
      // locations.forEach(location => {
      //   if (location?.address) console.log(formatAddress(location.address))
      // })


      // Iterate map markers
      for (let i = 0; i < markersRef.current.length; i++) {                                               
        const m = markersRef.current[i]

        // If we have a location, set position and glyph
        if (locations[i]?.address?.position !== undefined) {
          const position = formatPos(locations[i].address.position)
          m.marker.position = position
          m.exeptionZone = locations[i]?.mapExceptionZone || null;
          m.pin.glyph = 'ABCDEFGHIJKLMNOPQRST'[i]

          // If it is active, animate it
          if (i === active) {
            m.marker.content.addEventListener('animationend', () => { 
              m.marker.content.classList.remove(styles.dropAnim)
            })
            m.marker.content.classList.add(styles.dropAnim)
            zoomPosition(m.marker.position, m.exeptionZone)
          }

          // Remove any previously added listeners
          if (listenersRef[i]?.remove) listenersRef[i].remove()

          // Create and add click listener
          listenersRef[i] = m.marker.addListener('click', () => zoomPosition(m.marker.position, m.exeptionZone))
        }

        // Otherwise hide it
        else {
          m.marker.position = null
          if (listenersRef[i]?.remove) listenersRef[i].remove()
        }
      }
    }
  }

  const handleZoomLocation = location => {
    if (mapRef.current && location?.address?.position !== undefined) {
      clearTimeout(timeoutRef.current)

      // Iterate map markers and find the one matching location position
      for (let i = 0; i < markersRef.current.length; i++) {
        const m = markersRef.current[i]

        // Zoom to the marker
        if (m.marker?.position?.lat === location.address.position.latitude &&
            m.marker?.position?.lng === location.address.position.longitude) {
          zoomPosition(m.marker.position, m.exeptionZone)
        }
      }

    }
  }

  // Frame the current markers for the current viewport size. Split out of
  // handleDisplayTrip so a resize can re-frame without also clearing a drawn
  // exception zone.
  const frameMarkers = () => {
    if (mapRef.current && markersRef.current) {
      const numLocations = markersRef.current.reduce((a, m) => m.marker.position !== null ? a + 1 : a, 0)
      if (numLocations > 1) {
        const bounds = new google.maps.LatLngBounds()
        markersRef.current.forEach(m => {
          if (m?.marker?.position) bounds.extend(m.marker.position)
        })
        if (isMobile()) {
          const formHeight = getMobileBottomOffset() * 2
          mapRef.current.fitBounds(bounds, { top: 50, bottom: formHeight, left: 50, right: 50 })
        } else {
          mapRef.current.fitBounds(bounds, { top: 100, bottom: 250, left: 100, right: getDesktopRightOffset() })
        }
      }
      else {
        mapRef.current.setZoom(settings.zoom)

        const m = markersRef.current.find(m => m.marker.position !== null)
        if (m?.marker?.position) {
          mapRef.current.panTo(m.marker.position)
          if (!isMobile()) {
            mapRef.current.panBy(getDesktopRightOffset() / 2, 0) // desktop: adjust for right-side form panel
          } else {
            mapRef.current.panBy(0, getMobileBottomOffset()) // mobile: pan center down so marker appears above the form
          }
        }
      }
    }
  }

  const handleDisplayTrip = () => {
    if (mapRef.current) {
      clearTimeout(timeoutRef.current)
      resetExceptionsZone();
      frameMarkers()
    }
  }

  const handleReset = () => {
    if (mapRef.current) {
      clearTimeout(timeoutRef.current)
      markersRef.current.forEach(m => m.marker.position = null)
      mapRef.current.panTo(settings.center)
      mapRef.current.setZoom(settings.zoom)
    }
  }

  const zoomPosition = (position, exceptionZone) => {
    //if(exceptionsRef.current) return;
    if(exceptionZone) drawExeptionsZone({paths: exceptionZone.area.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] }))});
    else resetExceptionsZone();
    console.log('Zooming to position', position, exceptionZone);
    clearTimeout(timeoutRef.current)
    mapRef.current.setZoom(settings.zoom + 2)
    mapRef.current.panTo(position)
    if (isMobile()) {
      mapRef.current.panBy(0, getMobileBottomOffset()) // mobile: pan center down so marker appears above the form
    } else {
      mapRef.current.panBy(getDesktopRightOffset() / 2, 0) // desktop: adjust for right-side form panel
    }
    displayTrip()
  }

  const displayTrip = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(handleDisplayTrip, 5000)
  }

  
  useCustomEventListener('HOVER_LIST_ITEM', hoverListItem)
  useCustomEventListener('CLEAR_ITEM_HOVER', clearHoverListItem)
  useCustomEventListener('LOCATION_CHANGE', handleLocationChange)
  useCustomEventListener('DRAW_EXEPTIONS_ZONE', drawExeptionsZone)
  useCustomEventListener('ZOOM_LOCATION', handleZoomLocation)
  useCustomEventListener('DISPLAY_TRIP', handleDisplayTrip)
  useCustomEventListener('RESET_MAP', handleReset)

  useEffect(() => {
    if (!mapRef.current) {
      initMap()
    }
  })

  // Two jobs, both triggered by the container changing size.
  //
  // 1. A map constructed while its container is hidden (display:none) or zero-sized
  //    requests no tiles, so 'tilesloaded' never fires — isLoaded stays false and the
  //    map is left at opacity:0 permanently, even once the container is shown again.
  //    That is the "map never loads in fullscreen" case.
  // 2. Re-frame the markers. Preserving the old centre is not enough: the offsets
  //    that keep pins clear of the form panel differ between breakpoints and panel
  //    widths, so after a resize — or a mobile/desktop crossover — a centre computed
  //    for the old size leaves the pins under the panel. Only picking a new location
  //    re-framed before, which is why that looked correct and resizing did not.
  useEffect(() => {
    if (!elRef.current || !window.ResizeObserver) return

    let reframeTimer = null

    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      if (!width || !height || !mapRef.current) return

      setIsLoaded(true)

      // Debounced: a drag-resize fires this continuously.
      clearTimeout(reframeTimer)
      reframeTimer = setTimeout(frameMarkers, 150)
    })

    observer.observe(elRef.current)
    return () => {
      clearTimeout(reframeTimer)
      observer.disconnect()
    }
  }, [])

  return (
    <div ref={elRef} id="map" className={cx(styles.map, { isLoaded })} />
  )
}

const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className={styles.loader}>
          <Spinner />
        </div>
      )
    case Status.FAILURE:
      return 'Error'
    case Status.SUCCESS:
      return <MapComponent />
  }
}

const Map = () => <Wrapper apiKey={process.env.GOOGLE_API_KEY} render={render} />
export default Map
